import type { FC } from 'react';

import type { SvgIconProps } from '@mui/material/SvgIcon';

import { BlueprintIconsMap } from 'components/icons/blueprint/BlueprintIconsMap';

import type { BlueprintIconNames } from './BlueprintIconNames';
import { BlueprintIcons } from './BlueprintIcons';

/**
 * A centralized component for rendering blueprint-related icons. This component provides a unified way to use
 * icons throughout the application while ensuring optimal bundle size through tree-shaking.
 *
 * @remarks
 * All icons are rendered using @mui/material's SvgIcon component and support all its standard props.
 * The component utilizes tree-shaking, meaning only the icons you actually use will be included in the final bundle.
 *
 * @example
 * // Basic usage
 * <BlueprintIcon name="Action" />
 * <BlueprintIcon name="Action" fontSize="small" color="primary" />
 *
 * // Multiple icons in a component
 * <div>
 *   <BlueprintIcon name="Action" />
 *   <BlueprintIcon name="ActionList" />
 * </div>
 */
export const BlueprintIcon: FC<SvgIconProps & { name: string }> = ({ name, sx, ...props }) => {
  const mappedName = BlueprintIconsMap[name] || name;
  return (
    BlueprintIcons[mappedName as BlueprintIconNames]?.IconComponent({ sx, ...props }) ||
    BlueprintIcons.Node.IconComponent({ sx, ...props })
  );
};
